import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", kind: null });

  useEffect(() => {
    if (!sessionId) { setState({ phase: "error" }); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", kind: data.kind });
          return;
        }
      } catch (e) { /* retry */ }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setState({ phase: "timeout" });
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="w-full max-w-md border border-white/10 bg-[#0A0A0A] p-10 text-center">
        <div className="flex justify-center mb-8"><Logo compact /></div>
        {state.phase === "polling" && (
          <>
            <Loader2 className="w-10 h-10 text-brand animate-spin mx-auto" />
            <h1 className="font-display text-3xl text-white mt-6">Confirming your payment…</h1>
            <p className="text-sm text-zinc-400 mt-3">This takes a few seconds.</p>
          </>
        )}
        {state.phase === "paid" && (
          <>
            <CheckCircle2 data-testid="payment-confirmed-icon" className="w-10 h-10 text-brand mx-auto" />
            <h1 className="font-display text-4xl text-white mt-6">
              {state.kind === "order" ? "Order confirmed." : "Deposit received."}
            </h1>
            <p className="text-sm text-zinc-400 mt-4 leading-relaxed">
              {state.kind === "order"
                ? "Payment complete. Our team starts on your campaign within 24 hours — check your inbox for confirmation."
                : "Your booking request is in review. You'll get an email as soon as the team approves it."}
            </p>
            <Link to="/" data-testid="success-home-link"
              className="inline-block mt-8 bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase px-8 py-3.5 transition-colors">
              Back to Home
            </Link>
          </>
        )}
        {(state.phase === "timeout" || state.phase === "error") && (
          <>
            <Loader2 className="w-10 h-10 text-zinc-500 mx-auto" />
            <h1 className="font-display text-3xl text-white mt-6">Still processing</h1>
            <p className="text-sm text-zinc-400 mt-3">
              Your payment is being confirmed. You'll receive an email shortly — no need to pay again.
            </p>
            <Link to="/" className="inline-block mt-8 text-xs tracking-widest uppercase text-zinc-400 hover:text-brand transition-colors">
              Back to Home
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page" className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="w-full max-w-md border border-white/10 bg-[#0A0A0A] p-10 text-center">
        <div className="flex justify-center mb-8"><Logo compact /></div>
        <XCircle className="w-10 h-10 text-zinc-500 mx-auto" />
        <h1 className="font-display text-4xl text-white mt-6">Checkout cancelled.</h1>
        <p className="text-sm text-zinc-400 mt-4">Nothing was charged. Your selections are saved whenever you're ready.</p>
        <div className="flex justify-center gap-4 mt-8">
          <Link to="/marketing" data-testid="cancel-marketing-link"
            className="bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase px-6 py-3 transition-colors">
            Marketing
          </Link>
          <Link to="/" className="border border-white/20 hover:border-brand text-white text-xs font-semibold tracking-widest uppercase px-6 py-3 transition-colors">
            Home
          </Link>
        </div>
      </div>
    </div>
  );
}
